
import React from 'react';
import { Search, FileText, HelpCircle, Database, Shield } from "lucide-react";

const CoreFeatures = () => {
  return (
    <section id="core-features" className="py-20 px-6 bg-secondary">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-dark">Kernfunktionen unserer KI-Plattform</h2>
          <p className="text-grey text-lg max-w-3xl mx-auto">
            Eine umfassende Lösung, die Ihr gesamtes Unternehmenswissen intelligent nutzbar macht und 
            Mitarbeitern wertvolle Zeit spart.
          </p>
        </div>

        {/* Feature 1 */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <Search className="text-primary" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-dark">Interne Wissenssuche mit KI</h3>
              <p className="text-grey mb-6">
                Mitarbeiter durchsuchen per Chat sämtliche firmenspezifischen Dokumente und Datenbanken 
                in Sekundenschnelle – mit intelligenter semantischer Suche und natürlichsprachlichen Anfragen.
                Die KI versteht Kontext und liefert präzise Antworten statt langer Ergebnislisten.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Intelligente semantische Suche über alle Datenquellen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Natürlichsprachliche Anfragen statt komplexer Suchabfragen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Kontext-bewusstes Ranking der Suchergebnisse</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-custom">
              <img 
                src="/assets/images/laptop-code.jpg" 
                alt="KI-gestützte semantische Suche" 
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Feature 2 */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-white p-4 rounded-2xl shadow-custom">
                <img 
                  src="/assets/images/screens.jpg" 
                  alt="Dokumenten-Chat mit KI" 
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <FileText className="text-primary" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-dark">Dokumenten-Chat & Datenintegration</h3>
              <p className="text-grey mb-6">
                Ihr KI-Assistent bindet PDFs, Handbücher, Intranet-Seiten und weitere Datenquellen 
                nahtlos ein. Ingenieure im Maschinenbau können technische Zeichnungen und Handbücher 
                direkt abfragen, während Projektmanager wichtige Vertragsdaten sofort zur Hand haben.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Direkte Abfrage von PDFs, Handbüchern und Dokumenten</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Automatische Extraktion relevanter Informationen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Integration mit bestehenden Dokumentenmanagement-Systemen</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Feature 3 */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <HelpCircle className="text-primary" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-dark">Automatisierte FAQ-Erstellung</h3>
              <p className="text-grey mb-6">
                Aus vorhandenen Daten werden automatisch FAQs generiert und beantwortet. 
                Das System lernt kontinuierlich aus neuen Anfragen und erweitert die Wissensbasis – 
                ideal für Unternehmen mit hohem Support-Aufkommen in Kundenservice oder IT-Support.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Automatische Identifikation häufig gestellter Fragen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Kontinuierliche Erweiterung der Wissensbasis</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Drastische Entlastung von Support-Teams</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-custom">
              <img 
                src="/assets/images/workshop.jpg" 
                alt="Automatisierte FAQ-Erstellung" 
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Feature 4 */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-white p-4 rounded-2xl shadow-custom">
                <img 
                  src="/assets/images/desk.jpg" 
                  alt="API und Datenintegration" 
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <Database className="text-primary" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-dark">Schnittstellen & Datenquellen</h3>
              <p className="text-grey mb-6">
                Anbindung an alle gängigen Datenquellen wie Datenbanken, SharePoint, ERP und CRM-Systeme. 
                Über APIs und Vektordatenbanken wird Ihr bestehendes Wissen intelligent aufbereitet und 
                für die OpenWebUI-Integration nutzbar gemacht.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">API-Anbindung an bestehende Software-Landschaften</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Moderne Vektordatenbanken für optimale Suchergebnisse</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Flexible Connector-Architektur für individuelle Anpassungen</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Feature 5 */}
        <div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <Shield className="text-primary" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-dark">Datensicherheit & On-Premise-Betrieb</h3>
              <p className="text-grey mb-6">
                Höchste DSGVO-Konformität durch On-Premise-Betrieb auf Ihrer eigenen Infrastruktur. 
                Granulare Zugriffsrechte und vollständige Kontrolle über alle Daten schaffen Vertrauen 
                und erfüllen strengste Compliance-Anforderungen.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">100% On-Premise-Betrieb auf Ihrer eigenen Infrastruktur</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Granulare Zugriffssteuerung und Benutzerrechte</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="min-w-5 mt-1 text-primary">•</div>
                  <span className="text-grey">Audit-Trails und vollständige Protokollierung aller Aktivitäten</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-custom">
              <img 
                src="/assets/images/tablet.jpg" 
                alt="Datensicherheit und On-Premise-Betrieb" 
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreFeatures;
